// components/NotesSection.tsx
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { NormalFormTextArea } from '../molecules';
import { Button, RowSpacer } from '../atoms';
import { advancedNormalizeDate } from '@/helpers/normalizeDate';
import { useCallback, useRef, useState } from 'react';
import { useCreateNoteFromContractMutation } from '@/graphql/generated';
import textsData from '@/textData/ContractDetailTexts.json'
import {
  useContractDetailsContext,
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks';

const TEXT_LIMIT = 2000

const InputCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
`;

const NoteCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 1rem;
`;

const NoteText = styled.p`
  font-size: 0.95rem;
  color: #333;
  margin: 0 0 0.75rem 0;
`;

const NoteDate = styled.div`
  font-size: 0.8rem;
  color: #999;
`;

const NoteOwner = styled.div`
  font-size: 0.85rem;
  font-weight: bold;
  color: #555;
  margin-bottom: 0.25rem;
`;

const AvailableCharsWarn = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.s6};
  padding-left: 2rem;
`

export const ContractDetailNotesList = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.notes : textsData.es.notes
  
  const { contract, setContract } = useContractDetailsContext()
  
  const notes = contract?.notes

  const [availableChars, setAvailableChars] = useState(TEXT_LIMIT)
  
  const textRef = useRef<string | null>(null)

  const [createNote, {loading}] = useCreateNoteFromContractMutation()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()
  const {open: errorHandle} = useErrorHandlerContext()

  const onChangeHandle = useCallback((text: string | null) => {
    if (!text || text.length >= TEXT_LIMIT) return
    textRef.current = text;

    setAvailableChars(TEXT_LIMIT - (text.length || 0))
  }, [])

  const onSubmitHanddle = () => {
    createNote({ variables: {
      contractId: contract?.id || '',
      description: textRef.current || ''
    }})
      .then((response) => {
        confirmDialogModalOpen({
          title: texts.confirmDialog.title,
          description: `${texts.confirmDialog.description}. ID: ${response.data?.createNoteFromContract.id}`,
        })
        setContract(undefined) //Force state change on property context to force a refetch 
      })
      .catch(errorHandle)
  }

 
  return (
    <>
      <RowSpacer height={3} />
      <InputCard>
        <Row align="center">
          <Col>
            <NormalFormTextArea
              placeholder={texts.form.placeHolder}
              label={texts.form.label}
              onChange={onChangeHandle}
            />
          </Col>
        </Row>
        <Row justify='between'>
          <Col>
            <AvailableCharsWarn>
              <p>{`${availableChars} disponibles`}</p>
            </AvailableCharsWarn>
          </Col>
          <Col md="content">
            <Button
              isLoading={loading}
              onClick={onSubmitHanddle}
            >{texts.form.submitButton}</Button>
          </Col>
        </Row>
      </InputCard>
      {
        notes
        ? (
          notes.map((note) => (
            <NoteCard key={note.id}>
              <NoteOwner>
                {(note.ownerFirstName || note.ownerLastName)
                  ? `${note.ownerFirstName ?? ''} ${note.ownerLastName ?? ''}`.trim()
                  : ''}
              </NoteOwner>
              <NoteText>{note.text}</NoteText>
              <NoteDate>{advancedNormalizeDate(userLanguage, note.createdAt)}</NoteDate>
            </NoteCard>
          ))
        )
        : <h3>{texts.noData}</h3>
      }
    </>
  );
};
